/**
 * Porta de disponibilidade da identidade empresarial.
 *
 * Três estados diferentes se parecem na tela e não podem ser confundidos
 * (AGENTS §9, §18):
 *
 * - **não instalada**: o contrato server-side responde que a fundação não
 *   existe neste ambiente; a superfície fica visível e desabilitada;
 * - **degradada**: o contrato existe mas uma leitura protegida falhou; a
 *   escrita é bloqueada e o problema é operacional, não ausência de feature;
 * - **disponível**: leitura liberada. A escrita dos comandos candidatos ainda
 *   depende do demo ou do marcador de preparo do canário (§27.2): `canary_ready`
 *   sozinho nunca liga a criação canônica, e por isso ele só destrava os
 *   comandos já preparados, nunca a flag global.
 */
export interface IdentityGateInput {
  foundationAvailable: boolean;
  status: "available" | "not_installed" | "degraded";
  canaryReady: boolean;
  demoMode: boolean;
}

export interface IdentityGate {
  /** A leitura de identidade é confiável neste ambiente. */
  ready: boolean;
  degraded: boolean;
  /** Comandos de identidade (criar, mesclar, reclassificar, revisar) liberados. */
  commandsReady: boolean;
  /** Motivo único da indisponibilidade, pronto para `title` e texto acessível. */
  reason?: string;
}

export function identityGate(input: IdentityGateInput): IdentityGate {
  const { foundationAvailable, status, canaryReady, demoMode } = input;
  const degraded = status === "degraded";

  if (degraded)
    return {
      ready: false,
      degraded: true,
      commandsReady: false,
      reason:
        "Uma leitura protegida da identidade falhou. Corrija RLS, grants ou disponibilidade antes de escrever.",
    };

  if (!foundationAvailable || status === "not_installed")
    return {
      ready: false,
      degraded: false,
      commandsReady: false,
      reason:
        "A identidade empresarial não está instalada neste ambiente; o contrato de capacidades responde que ela não existe.",
    };

  if (!demoMode && !canaryReady)
    return {
      ready: true,
      degraded: false,
      commandsReady: false,
      reason:
        "Os comandos de identidade só estão preparados no staging marcado. A criação canônica continua desligada.",
    };

  return { ready: true, degraded: false, commandsReady: true };
}
